import { ApplicationException } from 'common/exceptions/application/ApplicationException';
import { Attachment } from 'common/models/Attachment';
import { SentimentPhraseDTO } from 'common/models/SentimentPhraseDTO';
import { ValidateFileRequest } from 'common/models/ValidateFileRequest';
import CONFIG from 'config';
import $ from 'jquery';
import { AddInFeatureNotSupportedException } from '../exceptions/application/AddInFeatureNotSupportedException';
import { CommunicationTemplate } from '../models/CommunicationTemplate';
import { Complaint } from '../models/Complaint';
import { Interaction } from '../models/Interaction';
import { OutlookAttachmentResponseDTO } from '../models/OutlookAttachmentResponseDTO';
import { SupportedOfficeItem } from '../utils/SupportedOfficeItem';
import * as Api from '../utils/api';
import { getDiffInHours, toIsoWithTimeZone } from '../utils/dateUtils';
import * as OutlookAsyncUtils from '../utils/outlookAsyncUtils';
import { getAsyncBody } from '../utils/outlookAsyncUtils';
import * as OutlookItemUtils from '../utils/outlookItemUtils';
import * as Ui from '../utils/uiUtils';
import { getStringValueOrNull } from '../utils/uiUtils';
import * as UserSettings from '../utils/user-settings';
import { Stakeholder } from './../models/Stakeholder';
import * as AppContacts from './contacts';
import * as ItemProperties from './item-properties';

const TAG = 'APS3: ';

export async function saveMessage(item: SupportedOfficeItem) {
  console.log(TAG, new Date(), 'Start saveMessage');
  const checks = [ensureEmailSubjectIsNotExcluded(item)];
  // don't check appointments because they will be checked by UID while saving
  if (item.itemType !== 'appointment') {
    checks.push(ensureEmailIsNotDuplicate(item));
  }

  console.log(TAG, new Date(), 'Execute message checks');
  await Promise.all(checks);

  console.log(TAG, new Date(), 'Get shared properties');
  const sharedProperties = await OutlookAsyncUtils.getSharedProperties(item);

  console.log(TAG, new Date(), 'Get stakeholders from ui');
  const stakeholders = Ui.getStakeholdersFromUi();

  console.log(TAG, new Date(), 'Save stakeholders');
  const stakeholdersIds = await saveStakeholders(stakeholders);

  const emailType = $('#item-communication').val();

  let communication: Interaction | Complaint;
  if (emailType == 'Communication' || !emailType) {
    console.log(TAG, new Date(), 'Save interaction');
    communication = await saveInteraction(
      item,
      sharedProperties,
      stakeholdersIds
    );
  } else {
    console.log(TAG, new Date(), 'Save complaint');
    communication = await saveComplaint(
      item,
      sharedProperties,
      stakeholdersIds
    );

    $('#save-project').prop('selectedIndex', 0);
  }

  console.log(TAG, new Date(), 'Mark item as saved');
  await ItemProperties.markItemAsSavedThroughApi(item);

  console.log(TAG, new Date(), 'Track vitally Event');
  await Api.trackVitallyEvent({ eventName: 'used_email_plugin' });

  Ui.showSaveSuccessfulDialog();
  $('#selectBox').hide();

  if (UserSettings.getAutosave()) {
    console.log(TAG, new Date(), 'Get date from outlook item');
    const firstSaveDate = await OutlookItemUtils.getDateFromOutlookItem(item);

    console.log(TAG, new Date(), 'Save autosave contact');
    saveAutosaveContact(
      firstSaveDate,
      communication,
      emailType,
      stakeholders[0]
    );
  }

  console.log(TAG, new Date(), 'End saveMessage');
}

async function ensureEmailIsNotDuplicate(item: SupportedOfficeItem) {
  if (CONFIG.allowToSaveMessageAgain) return;

  const response = await Api.findDuplicateEmail({
    Subject: await OutlookItemUtils.getSubjectFromOutlookItem(item),
    EmailAddresses: [
      item.from.emailAddress,
      ...item.to.map((to) => to.emailAddress),
    ],
    Message: await getAsyncBody(item.body),
    DateTime: toIsoWithTimeZone(
      await OutlookItemUtils.getDateFromOutlookItem(item)
    ),
  });

  if (response.IsMatchFound) {
    throw new ApplicationException(
      `Email was already saved in ${CONFIG.appName} as ${response.DocumentType}${response.DocumentID}`
    );
  }
}

async function ensureEmailSubjectIsNotExcluded(item: SupportedOfficeItem) {
  const isExcluded = await Api.ensureEmailSubjectIsNotExcluded({
    Subject: await OutlookItemUtils.getSubjectFromOutlookItem(item),
  });
  if (isExcluded) {
    throw new ApplicationException(
      'Email subject contains words that are in the exclusion list'
    );
  }
}

async function saveStakeholders(stakeholders: Stakeholder[]) {
  const savingStakeholders = [];
  stakeholders.forEach(function (stakeholder) {
    savingStakeholders.push(createOrUpdateStakeholder(stakeholder));
  });

  const stakeholdersIds = await Promise.all(savingStakeholders);
  return stakeholdersIds;
}

export async function saveInteraction(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined,
  stakeholderIds: string[]
) {
  console.log(TAG, 'Saving interaction', sharedProperties);

  // first, check if attachments are available and saved
  // so function would throw 'attachments not saved' error
  const attachmentsIds = await uploadAttachmentsIfSupported(
    item,
    sharedProperties
  );

  const interaction = await getInteractionFromUi(item, sharedProperties);

  if (CONFIG.isGraphApiEnabled && item.itemType === 'appointment') {
    const id = await OutlookItemUtils.getItemId(item, sharedProperties);

    const uids = await OutlookItemUtils.getAppointmentUids(
      id,
      sharedProperties
    );

    if (uids.itemType === 'seriesMaster') {
      throw new ApplicationException(
        'Saving series of events is not supported. Please select a single event in series.'
      );
    }

    console.log(TAG, 'Retrieved Appointment UID: ', uids.uid);
    interaction.MeetingUID = uids.uid;
    interaction.MeetingRecurrenceID = uids.recurrenceId;
  }

  const interactionId = await Api.saveInteraction(interaction);

  await Api.linkInteractionToStakeholders(interactionId, stakeholderIds);
  if (attachmentsIds.length > 0) {
    await Api.linkInteractionToAttachments(interactionId, attachmentsIds);
  }

  await Api.trackVitallyEvent({ eventName: 'new_interaction_saved' });

  return interaction;
}

export async function saveComplaint(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined,
  stakeholderIds: string[]
) {
  console.log(TAG, 'Saving complaint', sharedProperties);

  // first, check if attachments are available and saved
  // so function would throw 'attachments not saved' error
  const attachmentsIds = await uploadAttachmentsIfSupported(
    item,
    sharedProperties
  );

  const complaint = await getComplaintFromUi(item, sharedProperties);
  const interactionId = await Api.saveComplaint(complaint);

  await Api.linkComplaintToStakeholders(interactionId, stakeholderIds);
  if (attachmentsIds.length > 0) {
    await Api.linkComplaintToAttachments(interactionId, attachmentsIds);
  }

  await Api.trackVitallyEvent({ eventName: 'new_complaint_saved' });

  return complaint;
}

async function saveAutosaveContact(
  firstSaveDate: Date,
  communication: Interaction | Complaint,
  emailType: string | number | string[],
  stakeholder: Stakeholder
) {
  await AppContacts.createAutosaveContact(
    UserSettings.getProject(),
    emailType as string,
    stakeholder,
    communication as CommunicationTemplate,
    firstSaveDate
  );
}

async function getInteractionFromUi(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
) {
  const subject = await OutlookItemUtils.getSubjectFromOutlookItem(item);
  const location = await OutlookItemUtils.getLocationFromOutlookItem(item);
  const direction = OutlookItemUtils.getDirectionFromOutlookItem(
    item,
    sharedProperties
  );
  const dateTime = await OutlookItemUtils.getDateFromOutlookItem(item);
  const endDateTime = await OutlookItemUtils.getEndDateFromOutlookItem(item);
  const duration = getDiffInHours(endDateTime, dateTime);
  const body = await getAsyncBody(item.body);
  const confidentialValue = $('#item-confidential').val();

  let confidential = null;
  if (CONFIG.isConfidentialOptionsEnabled && confidentialValue) {
    confidential = { Value: confidentialValue };
  }

  let interactionType = null;
  const interactionTypeID = parseInt(
    getStringValueOrNull('#item-interaction-type')
  );
  if (interactionTypeID > 0) {
    interactionType = { Value: interactionTypeID };
  }

  const communicationDirection = await Api.getDirectionFromServer(direction);

  const interaction: Interaction = {
    Subject: subject,
    CommunicationDate: toIsoWithTimeZone(dateTime),
    Method: {
      Text: OutlookItemUtils.getInteractionMethodFromOutlookItem(item),
    },
    Direction: communicationDirection,
    Location: location,
    Message: body,
    Confidential: confidential || null,
    Duration: duration,
    InteractionType: interactionType,
    SentimentPhrases: $('#item-tag')
      .select2('data')
      ?.map((i) => {
        return {
          SentimentPhraseID: i.id,
          Phrase: i.text,
        } as SentimentPhraseDTO;
      }),
    AdditionalSharedProjectIDs: $('#save-project')
      .select2('data')
      ?.map((i) => i.id),
  };
  return interaction;
}

async function getComplaintFromUi(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
) {
  const subject = await OutlookItemUtils.getSubjectFromOutlookItem(item);
  const direction = OutlookItemUtils.getDirectionFromOutlookItem(
    item,
    sharedProperties
  );
  const dateTime = await OutlookItemUtils.getDateFromOutlookItem(item);
  const body = await getAsyncBody(item.body);

  const communicationDirection = await Api.getDirectionFromServer(direction);

  const confidentialValue = $('#item-confidential').val();
  const priorityValue = $('#complaint-priorities').val()
    ? $('#complaint-priorities').val()
    : '';
  const assignedTo = $('#item-assigned-to').val()
    ? $('#item-assigned-to').val()
    : '';
  const statusValue = $('#item-complaint-status').val()
    ? $('#item-complaint-status').val()
    : '';

  let confidential = null;
  if (CONFIG.isConfidentialOptionsEnabled && confidentialValue) {
    confidential = { Value: confidentialValue };
  }
  let priority = null;
  if (priorityValue) {
    priority = { Value: priorityValue.toString() };
  }
  let status = null;
  if (statusValue) {
    status = { Value: statusValue.toString() };
  }

  const complaint: Complaint = {
    Subject: subject,
    AssignedTo: {
      Value: assignedTo.toString(),
    },
    DateOpened: toIsoWithTimeZone(dateTime),
    Method: {
      Text: OutlookItemUtils.getInteractionMethodFromOutlookItem(item),
    },
    Direction: communicationDirection,
    Message: body,
    Priority: priority,
    Confidential: confidential,
    Status: status,
    InteractionType: null,
    SentimentPhrases: null,
  };

  return complaint;
}

async function uploadAttachmentsIfSupported(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
) {
  try {
    return await uploadAttachmentsForItem(item, sharedProperties);
  } catch (error) {
    if (error instanceof AddInFeatureNotSupportedException) {
      return [];
    } else {
      throw error;
    }
  }
}

export async function uploadAttachmentsForItem(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
) {
  if (!CONFIG.isAttachmentsEnabled) {
    console.log(TAG, 'Attachments are not enabled, skipping');
    return [];
  } else if (!OutlookItemUtils.canSaveAttachments()) {
    console.log(TAG, 'Attachments are not supported, skipping');
    return [];
  } else {
    console.log(TAG, 'Uploading attachments');
  }

  const attachments = await OutlookItemUtils.getSupportedAttachmentsForItem(
    item,
    sharedProperties
  );

  return await uploadAttachmentContent(attachments);
}

export async function uploadAttachmentsForItemId(
  itemId: string,
  itemType: string,
  sharedProperties: Office.SharedProperties | undefined
) {
  if (!CONFIG.isAttachmentsEnabled) {
    console.log(TAG, 'Attachments are not enabled, skipping');
    return [];
  } else {
    console.log(TAG, 'Uploading attachments');
  }

  const attachments = await OutlookItemUtils.getSupportedAttachmentsForItemId(
    itemId,
    itemType,
    sharedProperties
  );

  return await uploadAttachmentContent(attachments);
}

async function uploadAttachmentContent(attachments: Attachment[]) {
  if (attachments.length === 0) {
    return [];
  }

  for (const attachment of [...attachments]) {
    const attachmentSize = attachment.size > 0 ? attachment.size / 1024 : 0;
    const request: ValidateFileRequest = {
      FileName: attachment.name,
      SizeKb: attachmentSize,
    };

    console.log(
      TAG,
      'Validating attachment with Darzin API',
      attachment.name,
      attachment.size
    );

    const isValid = await Api.validateAttachment(request);
    if (!isValid) {
      const index = attachments.indexOf(attachment);
      attachments.splice(index, 1);
      console.log(TAG, 'Attachment is invalid');
    } else {
      console.log(TAG, 'Attachment is valid');
    }
  }

  const response = await Api.uploadOutlookAttachments({
    attachments: attachments.filter((a) => !a.isInline),
  });

  const responsesByAttachment: OutlookAttachmentResponseDTO[] =
    response.ResponsesByAttachment || [];

  const attachmentIds = responsesByAttachment
    .filter((a) => a.IsSuccess)
    .map((a) => a.FileID);

  return attachmentIds;
}

export async function createOrUpdateStakeholder(stakeholder: Stakeholder) {
  let stakeholderId = await Api.findStakeholder(stakeholder);

  // if stakeholder already exists, assign his ID to request
  // so stakeholder with this ID will be updated
  if (stakeholderId != '0') {
    // eslint-disable-next-line require-atomic-updates
    stakeholder.ID = stakeholderId;
    await Api.mergeStakeholderInfo(stakeholder);
  } else {
    stakeholderId = await Api.saveStakeholder(stakeholder);
  }

  return stakeholderId;
}
